import { generateClient } from 'aws-amplify/api'
import {
  listCallHistory as listCallHistoryQuery,
  listTaskDialAttempts as listTaskDialAttemptsQuery,
  getCallFeedback as getCallFeedbackQuery,
} from '@/graphql/queries'
import { submitCallFeedback as submitCallFeedbackMutation } from '@/graphql/mutations'
import { parseAwsJson } from '@/utils/graphql'
import { getApiMessage } from '@/mixins/Extras/ApiResponse'

const client = generateClient()

export async function fetchCallHistory(input = {}) {
  try {
    const { data, errors } = await client.graphql({
      query: listCallHistoryQuery,
      variables: { input },
    })

    const parsed = parseAwsJson(data?.listCallHistory)
    if (parsed != null) {
      return parsed
    }

    if (errors?.length) {
      throw new Error(errors[0].message)
    }

    return null
  } catch (error) {
    return {
      status: 'ERROR',
      message: getApiMessage(error, 'Failed to load call history'),
    }
  }
}

/**
 * Campaign-wide dial-attempt log -- every dial the campaign made, connected
 * or not (unlike fetchCallHistory above, which only ever returns calls that
 * connected long enough to leave a recording). Same thin pass-through
 * convention as fetchCallHistory: no param shaping here, the caller
 * (useTaskDialAttemptsData) builds the input object.
 */
export async function fetchTaskDialAttempts(input = {}) {
  try {
    const { data, errors } = await client.graphql({
      query: listTaskDialAttemptsQuery,
      variables: { input },
    })

    const parsed = parseAwsJson(data?.listTaskDialAttempts)
    if (parsed != null) {
      return parsed
    }

    if (errors?.length) {
      throw new Error(errors[0].message)
    }

    return null
  } catch (error) {
    return {
      status: 'ERROR',
      message: getApiMessage(error, 'Failed to load dial attempts'),
    }
  }
}

export async function fetchCallFeedback(callId) {
  if (!callId) return { status: 'ERROR', feedback: null }
  try {
    const { data, errors } = await client.graphql({
      query: getCallFeedbackQuery,
      variables: { input: { call_id: callId } },
    })
    if (errors?.length) throw new Error(errors[0].message)
    return parseAwsJson(data?.getCallFeedback) || { status: 'ERROR', feedback: null }
  } catch (error) {
    return {
      status: 'ERROR',
      feedback: null,
      message: getApiMessage(error, 'Failed to load feedback'),
    }
  }
}

export async function submitCallFeedback({ callId, orgId, rating, comment }) {
  try {
    const { data, errors } = await client.graphql({
      query: submitCallFeedbackMutation,
      variables: {
        input: { call_id: callId, org_id: orgId, rating, comment: comment || '' },
      },
    })
    if (errors?.length) throw new Error(errors[0].message)
    return parseAwsJson(data?.submitCallFeedback) || { status: 'ERROR' }
  } catch (error) {
    return {
      status: 'ERROR',
      message: getApiMessage(error, 'Failed to submit feedback'),
    }
  }
}
